import React, { useMemo } from 'react';
import { SiWhatsapp } from 'react-icons/si';
import { useAlert } from 'src/contexts/alert-context';
import {
  useDeleteWhatsAppIntegration,
  useWhatsAppIntegrations,
  useRecoverWhatsAppIntegration,
} from 'src/modules/shared/hooks/api/messaging/whatsapp/integrations';
import { useWhatsAppPhones } from 'src/modules/shared/hooks/api/messaging/whatsapp/phones';
import { Badge } from 'src/modules/shared/components/atoms/badge';
import { CircleDisplay } from 'src/modules/shared/components/atoms/circle-display';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { IntegrationCard } from 'src/modules/settings/components/molecules/integrations/integration-card';
import { WhatsAppIntegrationsListContent } from 'src/modules/settings/components/organisms/whatsapp/whatsapp-integrations-list/whatsapp-integrations-list-content';
import { IntegrationConnectionStatus } from 'src/api/generated';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { IntegrationPermission } from 'src/modules/shared/types/permissions.type';

const uniq = <T,>(values: T[]): T[] => Array.from(new Set(values));

export const WhatsAppAccountsCard: React.FC = () => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { hasPermission: canDeleteIntegration } = useHasPermissions(
    IntegrationPermission.Delete
  );
  const { hasPermission: canRecoverIntegration } = useHasPermissions(
    IntegrationPermission.Create
  );
  const { recoverWhatsAppIntegration, loading: recovering } =
    useRecoverWhatsAppIntegration(
      () =>
        showAlert(
          t('settings.integrations.whatsapp.recovery.success'),
          'success'
        ),
      () =>
        showAlert(t('settings.integrations.whatsapp.recovery.error'), 'error')
    );
  const {
    phones,
    loading: phonesLoading,
    error: phonesError,
  } = useWhatsAppPhones();
  const {
    integrations,
    loading: integrationsLoading,
    error: integrationsError,
  } = useWhatsAppIntegrations();

  const { deleteWhatsAppIntegration, loading: removing } =
    useDeleteWhatsAppIntegration(undefined, () => {
      showAlert(t('settings.integrations.whatsapp.errors.delete'), 'error');
    });

  const connectedAccountCount = useMemo(() => {
    if (phonesLoading || phonesError) return null;
    return uniq(phones.map((p) => p.integrationProviderId)).length;
  }, [phones, phonesError, phonesLoading]);

  const disconnectedAccountCount = useMemo(() => {
    if (integrationsLoading || integrationsError) return null;
    return integrations.filter(
      (integration) =>
        (integration.connectionStatus ?? IntegrationConnectionStatus.ACTIVE) !==
        IntegrationConnectionStatus.ACTIVE
    ).length;
  }, [integrations, integrationsError, integrationsLoading]);

  const subtitleParts = [
    connectedAccountCount !== null
      ? connectedAccountCount > 0
        ? t('settings.integrations.whatsapp.accounts.connectedCount', {
            count: connectedAccountCount,
          })
        : null
      : null,
    disconnectedAccountCount !== null
      ? disconnectedAccountCount > 0
        ? t('settings.integrations.whatsapp.accounts.disconnectedCount', {
            count: disconnectedAccountCount,
          })
        : null
      : null,
  ].filter(Boolean);

  const hasAccounts =
    (connectedAccountCount ?? 0) + (disconnectedAccountCount ?? 0) > 0;
  const accountsLoading = phonesLoading || integrationsLoading;

  if (!accountsLoading && !hasAccounts && !phonesError && !integrationsError) {
    return null;
  }

  return (
    <IntegrationCard
      title={t('settings.integrations.whatsapp.accounts.title')}
      subtitle={
        subtitleParts.length > 0 ? (
          <div className="flex items-center gap-2 flex-wrap">
            {connectedAccountCount !== null && connectedAccountCount > 0 ? (
              <Badge size="xs" variant="success" appearance="soft">
                {t('settings.integrations.whatsapp.accounts.connectedCount', {
                  count: connectedAccountCount,
                })}
              </Badge>
            ) : null}
            {disconnectedAccountCount !== null &&
            disconnectedAccountCount > 0 ? (
              <Badge size="xs" variant="secondary" appearance="soft">
                {t(
                  'settings.integrations.whatsapp.accounts.disconnectedCount',
                  {
                    count: disconnectedAccountCount,
                  }
                )}
              </Badge>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <TextSkeleton className="h-4 w-24 rounded-full" />
            <TextSkeleton className="h-4 w-28 rounded-full" />
          </div>
        )
      }
      cardProps={{ shadow: 'md', variant: 'primary', appearance: 'outline' }}
      icon={
        <CircleDisplay
          icon={<SiWhatsapp />}
          variant="whatsapp-subtle"
          size="md"
        />
      }
    >
      <WhatsAppIntegrationsListContent
        phones={phones}
        phonesLoading={phonesLoading}
        phonesError={phonesError}
        integrations={integrations}
        integrationsLoading={integrationsLoading}
        integrationsError={integrationsError}
        removing={removing}
        canDeleteIntegration={canDeleteIntegration}
        onRemoveIntegration={deleteWhatsAppIntegration}
        showSectionTitles={false}
        canRecoverIntegration={canRecoverIntegration}
        recovering={recovering}
        onRecoverIntegration={recoverWhatsAppIntegration}
      />
    </IntegrationCard>
  );
};
